<template>
    <div class="container">
        <schoolAndDate :schoolName="schoolName" :dateTime="dateTime" />
        <div class="wrap" ref="viewport">
            <transition name="page-turn-fade" mode="out-in">
                <div class="meal-list-wrap" :key="currentPageKey">
                    <div class="meal-list" v-for="(section, sectionIndex) in currentPage.sections" :key="section.sectionKey + '-' + sectionIndex">
                        <div class="module-block">
                            <div class="module-title">汇总</div>
                            <div class="summary-grid">
                                <div class="summary-cell" v-for="row in section.summaryRows" :key="row.rowKey">
                                    <div class="summary-label">{{ row.label }}</div>
                                    <div class="summary-value">{{ row.value }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="module-block module-gap">
                            <div class="module-title">明细</div>
                            <div class="detail-head" :ref="section.sectionKey + '-detailHead'">
                                <div class="detail-col name">物品</div>
                                <div class="detail-col unit">单位</div>
                                <div class="detail-col qty">数量</div>
                                <div class="detail-col price">单价</div>
                                <div class="detail-col amount">金额</div>
                            </div>
                            <div class="detail-row" v-for="detail in section.detailRows" :key="detail.rowKey">
                                <div class="detail-col name">{{ detail.name }}</div>
                                <div class="detail-col unit">{{ detail.unit }}</div>
                                <div class="detail-col qty">{{ detail.qtyText }}</div>
                                <div class="detail-col price">{{ detail.priceText }}</div>
                                <div class="detail-col amount">{{ detail.amountText }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <div class="measure-layer" aria-hidden="true">
                <div class="meal-list-wrap measure-wrap">
                    <div class="meal-list" v-for="(section, sectionIndex) in displaySections" :key="section.sectionKey + '-measure'">
                        <div class="module-block" :ref="'measureSummary-' + sectionIndex">
                            <div class="module-title">汇总</div>
                            <div class="summary-grid">
                                <div class="summary-cell" v-for="row in section.summaryRows" :key="row.rowKey">
                                    <div class="summary-label">{{ row.label }}</div>
                                    <div class="summary-value">{{ row.value }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="module-block module-gap">
                            <div class="module-title">明细</div>
                            <div class="detail-head" :ref="'measureDetailHead-' + sectionIndex">
                                <div class="detail-col name">物品</div>
                                <div class="detail-col unit">单位</div>
                                <div class="detail-col qty">数量</div>
                                <div class="detail-col price">单价</div>
                                <div class="detail-col amount">金额</div>
                            </div>
                            <div class="detail-row" v-for="(detail, rowIndex) in section.detailRows" :key="detail.rowKey" :ref="'measureRow-' + sectionIndex + '-' + rowIndex">
                                <div class="detail-col name">{{ detail.name }}</div>
                                <div class="detail-col unit">{{ detail.unit }}</div>
                                <div class="detail-col qty">{{ detail.qtyText }}</div>
                                <div class="detail-col price">{{ detail.priceText }}</div>
                                <div class="detail-col amount">{{ detail.amountText }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PageTurnControl class="page-turn-control-position" :current="currentPageIndex + 1" :total="pages.length" theme="meal" @prev="goPrevPage" @next="goNextPage" />
        </div>
    </div>
</template>

<script>
import { paginateMeasuredSections } from '../assets/js/contentPaging';
import { canAutoTurnPage, getNextPageIndex, getPrevPageIndex } from '../assets/js/pageTurn';
import { buildNutritiousDiningProcurementSummaryRows, buildNutritiousDiningProcurementDetailRows } from '../assets/js/nutritiousDiningProcurementSections';
import { formatTime } from '../assets/js/common';
import PageTurnControl from './PageTurnControl.vue';
import schoolAndDate from './schoolAndDate.vue';

export default {
    props: {
        deptId: {
            type: String,
            default: '',
        },
        outletsId: {
            type: String,
            default: '',
        },
        dateTime: {
            type: String,
            default: '',
        },
        schoolName: {
            type: String,
            default: '',
        },
    },
    components: { PageTurnControl, schoolAndDate },
    data() {
        return {
            procurementDetail: {},
            pages: [],
            currentPageIndex: 0,
            pageTimer: null,
        };
    },
    computed: {
        summaryRows() {
            return buildNutritiousDiningProcurementSummaryRows(this.procurementDetail);
        },
        detailRows() {
            return buildNutritiousDiningProcurementDetailRows(this.procurementDetail);
        },
        displaySections() {
            var summaryRows = this.summaryRows.map(function (row, index) {
                return {
                    rowKey: 'summary-row-' + index,
                    label: row && row.label ? row.label : '',
                    value: row && row.value ? row.value : '',
                };
            });
            var detailRows = this.detailRows.map(function (row, index) {
                return {
                    rowKey: 'detail-row-' + index,
                    name: row && row.name ? row.name : '',
                    unit: row && row.unit ? row.unit : '',
                    qtyText: row && row.qtyText ? row.qtyText : '',
                    priceText: row && row.priceText ? row.priceText : '',
                    amountText: row && row.amountText ? row.amountText : '',
                };
            });

            return [
                {
                    sectionKey: 'procurement',
                    sectionHeader: { startDate: '', endDate: '' },
                    summaryRows: summaryRows,
                    detailRows: detailRows,
                },
            ];
        },
        currentPage() {
            return this.pages[this.currentPageIndex] || { sections: [] };
        },
        currentPageKey() {
            return 'page-' + this.currentPageIndex;
        },
        autoplayDuration() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            var autoplay = query && query.autoplay ? parseInt(query.autoplay, 10) : 0;

            if (isNaN(autoplay) || autoplay <= 0) {
                return 0;
            }

            return autoplay;
        },
    },
    watch: {
        procurementDetail: {
            deep: true,
            handler: function () {
                this.rebuildPages();
            },
        },
        '$route.query.autoplay': function () {
            this.startPageTimer();
        },
    },
    mounted() {
        this.fetchProcurementDetail();
        window.addEventListener('resize', this.rebuildPages);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.rebuildPages);
        this.stopPageTimer();
    },
    methods: {
        fetchProcurementDetail() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            var procurementDate = query && query.procurementDate ? String(query.procurementDate) : '';

            // backward compatible fallback
            if (!procurementDate) {
                procurementDate = query && query.date ? String(query.date) : '';
            }

            if (!procurementDate) {
                procurementDate = formatTime(new Date(), 'yyyy-MM-dd');
            }

            var param = {
                canteenId: this.deptId,
                procurementDate: procurementDate,
            };

            this.$get(this.$api.getNutritiousDiningProcurementDateDetail, param).then((res) => {
                this.procurementDetail = res && res.data && typeof res.data === 'object' ? res.data : {};
            });
        },
        stopPageTimer() {
            if (this.pageTimer) {
                clearInterval(this.pageTimer);
                this.pageTimer = null;
            }
        },
        startPageTimer() {
            var vm = this;
            this.stopPageTimer();
            if (!canAutoTurnPage(this.pages.length, this.autoplayDuration)) {
                return;
            }
            this.pageTimer = setInterval(function () {
                vm.currentPageIndex = getNextPageIndex(vm.currentPageIndex, vm.pages.length);
            }, this.autoplayDuration);
        },
        restartPageTimer() {
            this.stopPageTimer();
            this.startPageTimer();
        },
        goPrevPage() {
            this.currentPageIndex = getPrevPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        goNextPage() {
            this.currentPageIndex = getNextPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        getRefElement(refName) {
            var refValue = this.$refs[refName];
            return Array.isArray(refValue) ? refValue[0] : refValue;
        },
        getMeasuredSection(section, sectionIndex) {
            var summaryEl = this.getRefElement('measureSummary-' + sectionIndex);
            var detailHeadEl = this.getRefElement('measureDetailHead-' + sectionIndex);
            var rows = section.detailRows.map(function (row, rowIndex) {
                var rowEl = this.getRefElement('measureRow-' + sectionIndex + '-' + rowIndex);
                return {
                    rowKey: row.rowKey,
                    measuredHeight: rowEl ? rowEl.offsetHeight : 0,
                    payload: row,
                };
            }, this);

            return {
                sectionKey: section.sectionKey,
                sectionHeader: section.sectionHeader,
                headerHeight: (summaryEl ? summaryEl.offsetHeight : 0) + (detailHeadEl ? detailHeadEl.offsetHeight : 0) + 24,
                rows: rows,
            };
        },
        getMeasureWrapInset() {
            var measureWrap = this.$el ? this.$el.querySelector('.measure-wrap') : null;
            var styles;
            if (!measureWrap) {
                return 0;
            }
            styles = window.getComputedStyle(measureWrap);
            return (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
        },
        getSectionGap() {
            var firstSection = this.$el ? this.$el.querySelector('.measure-wrap .meal-list') : null;
            var styles;
            if (!firstSection) {
                return 0;
            }
            styles = window.getComputedStyle(firstSection);
            return parseFloat(styles.marginBottom) || 0;
        },
        rebuildPages() {
            this.$nextTick(function () {
                var viewport = this.$refs.viewport;
                var pageInset = this.getMeasureWrapInset();
                var sectionGap = this.getSectionGap();
                var measuredSections = this.displaySections.map(function (section, index) {
                    return this.getMeasuredSection(section, index);
                }, this);

                this.pages = paginateMeasuredSections({
                    maxPageHeight: viewport ? viewport.clientHeight : 0,
                    pageInset: pageInset,
                    sectionGap: sectionGap,
                    sections: measuredSections,
                }).map(function (page) {
                    return {
                        pageIndex: page.pageIndex,
                        sections: page.sections.map(function (section) {
                            return {
                                sectionKey: section.sectionKey,
                                sectionHeader: section.sectionHeader,
                                continued: section.continued,
                                summaryRows: [],
                                detailRows: section.rows.map(function (row) {
                                    return row.payload;
                                }),
                            };
                        }),
                    };
                });

                // rebuild pages should preserve summary on every page
                this.pages = this.pages.map(function (page) {
                    var summaryRows = this.displaySections[0] ? this.displaySections[0].summaryRows : [];
                    return {
                        pageIndex: page.pageIndex,
                        sections: page.sections.map(function (section) {
                            return {
                                sectionKey: section.sectionKey,
                                sectionHeader: section.sectionHeader,
                                continued: section.continued,
                                summaryRows: summaryRows,
                                detailRows: section.detailRows,
                            };
                        }, this),
                    };
                }, this);

                if (this.currentPageIndex >= this.pages.length) {
                    this.currentPageIndex = 0;
                }

                this.startPageTimer();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
$fontColor: #0A7B23;
$borderColor: #59C06F;

.wrap {
    position: relative;
    height: vh(785);
    background: #b2f5c2;
    border-radius: 3px;
    overflow: hidden;
    font-weight: bold;
}

.meal-list-wrap {
    padding: vh(12) vh(18) vh(58);
}

.meal-list {
    margin-bottom: vh(10);
}

.module-block {
    border: 1px solid $borderColor;
    color: $fontColor;
    background: transparent;
    border-radius: vh(10);
    overflow: hidden;
}

.module-gap {
    margin-top: vh(12);
}

.module-title {
    background: #8EDE9F;
    padding: vh(6) vh(8);
    font-weight: 600;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-top: 1px solid $borderColor;
}

.summary-cell {
    padding: vh(6) vh(8);
    border-right: 1px solid $borderColor;
    border-bottom: 1px solid $borderColor;
    box-sizing: border-box;

    &:nth-of-type(4n) {
        border-right: 0;
    }
}

.summary-label {
    font-size: vh(14);
    color: $fontColor;
    opacity: 0.95;
}

.summary-value {
    font-size: vh(16);
    color: $fontColor;
    word-break: break-all;
    line-height: 1.2;
}

.module-row {
    display: flex;
    border-top: 1px solid $borderColor;
}

.module-label {
    width: 35%;
    padding: vh(6) vh(8);
    border-right: 1px solid $borderColor;
    box-sizing: border-box;
}

.module-value {
    width: 65%;
    padding: vh(6) vh(8);
    box-sizing: border-box;
    word-break: break-all;
}

.detail-head,
.detail-row {
    display: flex;
    border-top: 1px solid $borderColor;
}

.detail-head {
    background: #8EDE9F;
    font-weight: 600;
}

.detail-col {
    padding: vh(6) vh(6);
    box-sizing: border-box;
    word-break: break-all;
    border-right: 1px solid $borderColor;
}

.detail-col:last-child {
    border-right: 0;
}

.detail-col.name { width: 34%; }
.detail-col.unit { width: 12%; text-align: center; }
.detail-col.qty { width: 14%; text-align: center; }
.detail-col.price { width: 20%; text-align: center; }
.detail-col.amount { width: 20%; text-align: center; }

.measure-layer {
    position: absolute;
    left: -9999px;
    top: 0;
    width: 100%;
    height: auto;
    opacity: 0;
    pointer-events: none;
}

.page-turn-control-position {
    position: absolute;
    left: 50%;
    bottom: vh(8);
    z-index: 8;
    transform: translateX(-50%);
}

.page-turn-fade-enter-active,
.page-turn-fade-leave-active {
    transition: opacity 0.3s ease;
}

.page-turn-fade-enter,
.page-turn-fade-leave-to {
    opacity: 0;
}
</style>
